import * as Sentry from '@sentry/react';
import { AvatarGroup } from '@stratumn/atomic';
import to from 'await-to-js';
import classNames from 'classnames';
import { EditableText, UploadPhoto } from 'components/ui';
import compose from 'lodash.flowright';
import { updateOrganization } from 'mutations';
import PropType from 'prop-types';
import { useState } from 'react';
import injectSheet from 'react-jss';
import { toast } from 'sonner';
import { SpanType, withSpanAsync } from 'tracing';
import nameToInitials from 'utils/nameToInitials';
import { withGqlClient, withRestClient } from 'wrappers';

import styles from './settings.style';

export function Settings({ classes, client, environment, organization }) {
  const { avatar, canUpdate, name, rowId } = organization;
  const [orgName, setOrgName] = useState(name || '');

  const editOrganization = (newAvatar, newName) =>
    withSpanAsync('updateOrganization', SpanType.processing, async () => {
      let newAvatarUrl;

      if (newAvatar instanceof File) {
        const { digest } = await client.media.uploadFile(newAvatar, {
          disableEncryption: true
        });
        newAvatarUrl = await client.media.buildDownloadURL(digest);
      }

      const [err] = await to(
        updateOrganization(environment, rowId, newName, newAvatarUrl)
      );

      if (err) {
        Sentry.captureException(err);
        toast.error(`Couldn't save the organization changes`);
      } else if (newAvatarUrl) {
        toast.success(`Saved new Organization's avatar`);
      } else {
        toast.success(`Saved new Organization's name ${newName.toUpperCase()}`);
      }
    });

  const editInput = input => {
    if (input !== orgName) {
      setOrgName(input);
      editOrganization(null, input);
    }
  };

  return (
    <div className={classes.page}>
      <h1 className={classes.heading}>Organization settings</h1>
      <div className={classes.orgTitle}>
        {canUpdate ? (
          <UploadPhoto
            avatar={avatar}
            data-testid="upload-photo"
            getNewAvatar={editOrganization}
            name={name}
            overlayOnHover
            size={40}
          />
        ) : (
          <AvatarGroup
            initials={nameToInitials(name)}
            onClick={editOrganization}
            size={40}
            src={avatar}
          />
        )}
        {canUpdate ? (
          <div className={classNames(classes.orgName, classes.canUpdateInput)}>
            <EditableText
              data-testid="editable-text"
              getInput={editInput}
              input={orgName}
              placeholder="Organization's name"
            />
          </div>
        ) : (
          <div className={classes.orgName}>{name}</div>
        )}
      </div>
    </div>
  );
}

Settings.propTypes = {
  classes: PropType.object.isRequired,
  client: PropType.object.isRequired,
  environment: PropType.object.isRequired,
  organization: PropType.object.isRequired
};

export default compose(
  withGqlClient,
  withRestClient,
  injectSheet(styles)
)(Settings);
